import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FrameHistoryAdapted } from 'src/app/historial/models/manager-content-vehicle-history-drawer.model';

interface TechnicalParameter {
  key: string;
  value: any;
  timestamp: Date;
  frameIndex: number;
}

interface FilteredParameter extends TechnicalParameter {
  displayValue: string;
}

@Component({
  selector: 'technical-parameters',
  templateUrl: './technical-parameters.component.html',
  styleUrls: ['./technical-parameters.component.scss'],
})
export class TechnicalParametersComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() frames: FrameHistoryAdapted[] = [];
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @ViewChild('virtualScroll', { static: false })
  virtualScroll!: CdkVirtualScrollViewport;

  private destroy$ = new Subject<void>();

  // Controles de filtros
  searchControl = new FormControl('');
  keyTypeControl = new FormControl('all');
  specificDateControl = new FormControl('');
  startTimeControl = new FormControl('');
  endTimeControl = new FormControl('');
  frameNumberControl = new FormControl('');
  specificTypeControl = new FormControl('');
  specificValueControl = new FormControl('');

  // Estado del toggle de filtros avanzados
  showAdvancedFilters = false;

  // Datos
  allParameters: TechnicalParameter[] = [];
  filteredParameters: FilteredParameter[] = [];
  availableKeyTypes: string[] = [];
  availableKeyTypesFormatted: { label: string; value: string }[] = [];

  // Virtual scroll
  itemSize = 48;

  constructor() {}

  ngOnInit(): void {
    this.setupFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Forzar la detección de cambios para el virtual scroll
    this.initializeVirtualScroll();
  }

  ngOnChanges(): void {
    if (this.frames && this.frames.length > 0) {
      this.extractParameters();
      this.applyFilters();
    }

    // Reinicializar virtual scroll cuando el diálogo se hace visible
    if (this.visible && this.virtualScroll) {
      setTimeout(() => this.initializeVirtualScroll(), 50);
    }
  }

  private initializeVirtualScroll(): void {
    if (this.virtualScroll) {
      // Inicialización específica solo para este componente
      setTimeout(() => {
        // Solo verificar el tamaño del viewport de este componente específico
        if (this.virtualScroll && this.virtualScroll.elementRef) {
          this.virtualScroll.checkViewportSize();
          this.virtualScroll.scrollToIndex(0);
        }
      }, 100);
    }
  }

  private setupFilters(): void {
    // Configurar filtros reactivos
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.keyTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.specificDateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.startTimeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.endTimeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.frameNumberControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.specificTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.specificValueControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
  }

  private extractParameters(): void {
    this.allParameters = [];
    const keyTypesSet = new Set<string>();

    this.frames.forEach((frame, frameIndex) => {
      if (frame.IOs) {
        Object.entries(frame.IOs).forEach(([key, value]) => {
          keyTypesSet.add(key);

          // Manejar correctamente el formato de fecha "2025/09/04 08:35:42"
          let timestamp: Date;
          if (typeof frame.date === 'string') {
            // Si la fecha viene en formato "2025/09/04 08:35:42", convertirla correctamente
            const dateStr = frame.date.replace(/\//g, '-');
            timestamp = new Date(dateStr);

            // Si la conversión falla, intentar con formato ISO en zona horaria local
            if (isNaN(timestamp.getTime())) {
              const isoFormat = dateStr.replace(' ', 'T');
              timestamp = new Date(isoFormat);
            }
          } else {
            timestamp = new Date(frame.date);
          }

          this.allParameters.push({
            key,
            value,
            timestamp,
            frameIndex,
          });
        });
      }
    });

    // Ordenar tipos alfabéticamente (A-Z)
    this.availableKeyTypes = Array.from(keyTypesSet).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    // Formatear para el dropdown manteniendo "Todos los tipos" como primera opción
    this.availableKeyTypesFormatted = [
      { label: 'Todos los tipos', value: '' },
      ...this.availableKeyTypes.map((keyType) => ({
        label: keyType,
        value: keyType,
      })),
    ];
  }

  applyFilters(): void {
    let filtered = [...this.allParameters];

    // Filtro por tipo de key
    const selectedKeyType = this.keyTypeControl.value;
    if (selectedKeyType && selectedKeyType !== 'all') {
      filtered = filtered.filter((param) => param.key === selectedKeyType);
    }

    // Filtro por número de tramo/serie específico
    const frameNumber = this.frameNumberControl.value;
    if (frameNumber) {
      filtered = filtered.filter(
        (param) => param.frameIndex + 1 === Number(frameNumber)
      );
    }

    // Filtro por tipo específico
    const specificType = this.specificTypeControl.value?.toLowerCase();
    if (specificType) {
      filtered = filtered.filter((param) =>
        param.key.toLowerCase().includes(specificType)
      );
    }

    // Filtro por valor específico
    const specificValue = this.specificValueControl.value?.toLowerCase();
    if (specificValue) {
      filtered = filtered.filter((param) =>
        String(param.value).toLowerCase().includes(specificValue)
      );
    }

    // Filtro por fecha específica y horarios
    const specificDate = this.specificDateControl.value;
    const startTime = this.startTimeControl.value;
    const endTime = this.endTimeControl.value;

    if (specificDate) {
      // Crear fecha objetivo en zona horaria local para evitar problemas de UTC
      const targetDate = new Date(specificDate + 'T00:00:00');

      if (startTime || endTime) {
        // Filtrar por fecha y rango horario
        filtered = filtered.filter((param) => {
          const paramDate = new Date(param.timestamp);

          // Verificar que sea el mismo día usando comparación de año, mes y día
          const isSameDay =
            paramDate.getFullYear() === targetDate.getFullYear() &&
            paramDate.getMonth() === targetDate.getMonth() &&
            paramDate.getDate() === targetDate.getDate();

          if (!isSameDay) {
            return false;
          }

          if (startTime) {
            const startDateTime = new Date(startTime);
            const paramTime =
              paramDate.getHours() * 3600 +
              paramDate.getMinutes() * 60 +
              paramDate.getSeconds();
            const startTimeSeconds =
              startDateTime.getHours() * 3600 +
              startDateTime.getMinutes() * 60 +
              startDateTime.getSeconds();
            if (paramTime < startTimeSeconds) return false;
          }

          if (endTime) {
            const endDateTime = new Date(endTime);
            const paramTime =
              paramDate.getHours() * 3600 +
              paramDate.getMinutes() * 60 +
              paramDate.getSeconds();
            const endTimeSeconds =
              endDateTime.getHours() * 3600 +
              endDateTime.getMinutes() * 60 +
              endDateTime.getSeconds();
            if (paramTime > endTimeSeconds) return false;
          }

          return true;
        });
      } else {
        // Filtrar solo por fecha (todo el día)
        filtered = filtered.filter((param) => {
          const paramDate = new Date(param.timestamp);
          // Usar comparación más precisa para fechas, evitando problemas de zona horaria
          const isSameDay =
            paramDate.getFullYear() === targetDate.getFullYear() &&
            paramDate.getMonth() === targetDate.getMonth() &&
            paramDate.getDate() === targetDate.getDate();
          return isSameDay;
        });
      }
    }

    // Filtro de búsqueda general (key, valor, número de trama)
    const searchTerm = this.searchControl.value?.toLowerCase();
    if (searchTerm) {
      filtered = filtered.filter(
        (param) =>
          param.key.toLowerCase().includes(searchTerm) ||
          String(param.value).toLowerCase().includes(searchTerm) ||
          String(param.frameIndex + 1).includes(searchTerm)
      );
    }

    // Convertir a FilteredParameter con displayValue
    this.filteredParameters = filtered.map((param) => ({
      ...param,
      displayValue: this.formatValue(param.value),
    }));
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      // En caso que sea null o undefined devolvemos '-'
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Verdadero' : 'Falso';
    }

    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    return String(value);
  }

  clearFilters(): void {
    this.searchControl.setValue('');
    this.keyTypeControl.setValue('all');
    this.specificDateControl.setValue('');
    this.startTimeControl.setValue('');
    this.endTimeControl.setValue('');
    this.frameNumberControl.setValue('');
    this.specificTypeControl.setValue('');
    this.specificValueControl.setValue('');
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.applyFilters();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  trackByParameter(index: number, item: FilteredParameter): string {
    return `${item.frameIndex}-${item.key}`;
  }

  onDialogHide(): void {
    this.visibleChange.emit(false);
  }
}
